/**
 * 開発ツリー初期子票の構成（純データ・SSOT）。
 *
 * 「開発が始まる母票には最初からこの 7 種の子票が並んでいる」という運用を、**作られ方に
 * 依存せず 1 箇所**で定義する。読み手は 3 つある:
 *
 * - 案件起票（`apps/api/product` の project-intake）— 起票フォームから母票と一緒に作る
 * - バグ票の自動付与（`apps/pipeline/backlog-webhook`）— バグ票は起票ボタンを通らず Backlog で
 *   直接作られるため、作成を検知してから同じ子票群を後付けする
 * - 予定pt の補完（`apps/pipeline/backlog-pmo-manager` の処理11）— ざっくり見積が母票へ入った
 *   後に、`estimatePercent` を持つ種別の予定pt を算出して埋める
 *
 * 片方にだけ種別を足す / 担当ロールを変えると、「同じ開発フローなのに入口によって子票が違う」
 * 状態になる（実際に 2026-09 の 3 種追加時はバグ票が取り残された）。ここを唯一の定義にして
 * 構造的に防ぐ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 開発ツリー初期子票（課題種別・件名ラベル・担当ロール・予定pt 比率・軽タスク除外・要件定義セット所属）の構成定義。要件定義セットは KPI が開発pt から要件定義分を切り出す述語の正本でもある
 * @graph-connects none
 */
/**
 * 開発ツリー初期子票の Backlog 課題種別 ID。
 *
 * 同じ値を `apps/api/product` の `BACKLOG_IDS` が独自に持っていたのを、この定義の参照へ寄せた。
 * `apps/pipeline/backlog-pmo-manager` の `ISSUE_TYPE` にも一部の種別の独立コピーが残っている
 * （処理11 の予定pt 補完はここを読むようになったが、他の処理が使う種別は未移行で SSOT 化の
 * 対象として残課題）。
 * @graph-connects none
 */
export const DEV_TREE_ISSUE_TYPE_IDS = {
    /** ざっくり見積もりレビュー。 */
    ROUGH_ESTIMATE_REVIEW: 1291809,
    /** 業務フロー図作成。 */
    BUSINESS_FLOW_MAKE: 1177559,
    /** バリデーションリスト作成。 */
    VALIDATION_LIST_MAKE: 1178508,
    /** 機能一覧作成。 */
    FEATURE_LIST_MAKE: 1281879,
    /** 要件定義レビュー。 */
    REQUIREMENT_REVIEW: 1291811,
    /** 総合テスト作成。 */
    SYSTEM_TEST_MAKE: 702066,
    /** 開発インプット。 */
    DEV_INPUT: 734521,
};
/**
 * 完了更新日カスタムフィールド ID（日付型）。
 *
 * 子票が「完了」になったときに実際の完了日（JST）を書き込む欄で、`issues_normalized.completed_at`
 * もこのフィールド由来。**子票を完了にする経路が複数ある**ため、ID をここ 1 箇所に置く——
 * backlog-webhook の補完ハンドラ（人が Backlog で完了にした場合）と product-api の要件定義
 * 承認（PI Lab / MCP から完了にする場合）が同じ欄を書く。片方だけ別 ID を持つと、
 * 入口によって完了日が入ったり入らなかったりする。
 * @graph-connects none
 */
export const COMPLETION_UPDATE_DATE_CF_ID = 112302;
/**
 * 開発ツリー初期子票の定義。
 *
 * 並び順は開発フローの工程順（ざっくり見積もりレビュー → 業務フロー図作成 → バリデーション
 * リスト作成 → 機能一覧作成 → 要件定義レビュー → 総合テスト作成 → 開発インプット）。Backlog の
 * 子チケット一覧は作成順に並ぶため、**配列順がそのまま人が見る並び**になる。並べ替えると既存
 * 案件と新規案件で並びが変わる。
 *
 * 要件定義の成果物は「要件定義作成」1 種から **業務フロー図作成 / バリデーションリスト作成 /
 * 機能一覧作成** の 3 種へ分割した（2026-10 の開発フロー変更）。成果物ごとに完了と実績pt を
 * 追えるようにするための分割で、1 枚にまとめていると「どの成果物がどこまで出来ているか」が
 * 票からは分からなかった。種別 `1291810`（要件定義作成）は既定作成から外れ、既存票のためだけに
 * Backlog へ残っている。
 *
 * `lightTaskExcluded` の 5 種（ざっくり見積もりレビュー / 要件定義の成果物 3 種 /
 * 要件定義レビュー）は「軽タスク」では作らない。軽タスクは文言修正・画像差し替え等の小規模
 * 作業で、ざっくり見積も要件定義も置かずにアサインへ進む運用のため、作っても未対応のまま残る。
 * @graph-connects none
 */
export const DEV_CHILD_SPECS = [
    {
        label: 'ざっくり見積もりレビュー',
        summaryLabel: 'ざっくり見積もりレビュー_Rough Estimate Review',
        issueTypeId: DEV_TREE_ISSUE_TYPE_IDS.ROUGH_ESTIMATE_REVIEW,
        assigneeRole: 'techLead',
        estimatePercent: undefined,
        lightTaskExcluded: true,
        requirementDefinitionSet: false,
    },
    {
        label: '業務フロー図作成',
        summaryLabel: '業務フロー図作成_Business Flow Creation',
        issueTypeId: DEV_TREE_ISSUE_TYPE_IDS.BUSINESS_FLOW_MAKE,
        assigneeRole: 'requirementAssign',
        estimatePercent: 3,
        lightTaskExcluded: true,
        requirementDefinitionSet: true,
    },
    {
        label: 'バリデーションリスト作成',
        summaryLabel: 'バリデーションリスト作成_Validation List Creation',
        issueTypeId: DEV_TREE_ISSUE_TYPE_IDS.VALIDATION_LIST_MAKE,
        assigneeRole: 'requirementAssign',
        estimatePercent: 4,
        lightTaskExcluded: true,
        requirementDefinitionSet: true,
    },
    {
        label: '機能一覧作成',
        summaryLabel: '機能一覧作成_Feature List Creation',
        issueTypeId: DEV_TREE_ISSUE_TYPE_IDS.FEATURE_LIST_MAKE,
        assigneeRole: 'requirementAssign',
        estimatePercent: 4,
        lightTaskExcluded: true,
        requirementDefinitionSet: true,
    },
    {
        label: '要件定義レビュー',
        summaryLabel: '要件定義レビュー_Requirement Definition Review',
        issueTypeId: DEV_TREE_ISSUE_TYPE_IDS.REQUIREMENT_REVIEW,
        assigneeRole: 'dle',
        estimatePercent: undefined,
        lightTaskExcluded: true,
        requirementDefinitionSet: true,
    },
    {
        label: '総合テスト作成',
        summaryLabel: '総合テスト作成_UAT Creation',
        issueTypeId: DEV_TREE_ISSUE_TYPE_IDS.SYSTEM_TEST_MAKE,
        assigneeRole: 'requirementAssign',
        estimatePercent: 4,
        lightTaskExcluded: false,
        requirementDefinitionSet: true,
    },
    {
        label: '開発インプット',
        summaryLabel: '開発インプット_Dev Input',
        issueTypeId: DEV_TREE_ISSUE_TYPE_IDS.DEV_INPUT,
        assigneeRole: 'pl',
        estimatePercent: undefined,
        lightTaskExcluded: false,
        requirementDefinitionSet: false,
    },
];
/**
 * 要件定義アサインで担当エンジニアを立てる子票の課題種別 ID（工程順）。
 *
 * **順序が結果表示の順序**になる。要件定義の成果物 3 種と総合テスト作成は、要件定義アサインで
 * 決まった 1 人のエンジニアがまとめて持つ運用。
 * @graph-connects none
 */
export const REQUIREMENT_ASSIGN_CHILD_TYPE_IDS = DEV_CHILD_SPECS.filter((spec) => spec.assigneeRole === 'requirementAssign').map((spec) => spec.issueTypeId);
/** 要件定義セット（`requirementDefinitionSet`）の子票定義（工程順）。 @graph-connects none */
const REQUIREMENT_DEFINITION_SET_SPECS = DEV_CHILD_SPECS.filter((spec) => spec.requirementDefinitionSet);
/**
 * 要件定義セットの Backlog 課題種別 ID（工程順）。
 *
 * Backlog webhook / PMO バッチのように課題種別 ID で判定する側が使う。
 * @graph-connects none
 */
export const REQUIREMENT_DEFINITION_SET_ISSUE_TYPE_IDS = REQUIREMENT_DEFINITION_SET_SPECS.map((spec) => spec.issueTypeId);
/**
 * 要件定義セットの Backlog 課題種別名（工程順）。
 *
 * `kpi_develop_point_detail_table` は `issue_type_id` を持たず `issue_type_name` だけを
 * 引き継ぐため、KPI の実績pt 集計（開発pt から要件定義分を切り出す述語）はこちらを使う。
 * **「総合テスト作成」と前方一致する別種別（`総合テスト作成レビュー`）が実在する**ので、
 * 述語は必ず完全一致（`IN UNNEST`）で当てること（`LIKE '総合テスト作成%'` は別工程を巻き込む）。
 * @graph-connects none
 */
export const REQUIREMENT_DEFINITION_SET_ISSUE_TYPE_NAMES = REQUIREMENT_DEFINITION_SET_SPECS.map((spec) => spec.label);
/**
 * 要件定義セットのうち**成果物だけ**の Backlog 課題種別名（= 要件定義セット − 要件定義レビュー）。
 *
 * 要件定義レビューは成果物を作る工程ではなく、DLE が他人の成果物を見るレビュー工程
 * （`assigneeRole: 'dle'`。他の 4 工程は `requirementAssign` = 要件定義担当の作業）。
 * 「この人がこれから作る要件定義をどれだけ抱えているか」を数える用途では、レビュー待ちの票を
 * 混ぜると**本人の作業ではない札**が積み上がって実態より多く見える（実データでは未対応の
 * 要件定義票の大半がレビュー票だった）。そうした**先行き（未着手）の量を数える述語**はこちらを使う。
 *
 * 実績pt の切り出し（KPI の「開発pt（要件定義を除く）」や戦闘力サマリーの要件定義pt）は
 * レビュー工数も要件定義に使った時間なので、従来どおり
 * `REQUIREMENT_DEFINITION_SET_ISSUE_TYPE_NAMES`（5 種全部）を使うこと。
 * @graph-connects none
 */
export const REQUIREMENT_DELIVERABLE_ISSUE_TYPE_NAMES = REQUIREMENT_DEFINITION_SET_SPECS.filter((spec) => spec.issueTypeId !== DEV_TREE_ISSUE_TYPE_IDS.REQUIREMENT_REVIEW).map((spec) => spec.label);
/**
 * 要件定義セットのうち**成果物だけ**の Backlog 課題種別 ID（工程順）。
 * `REQUIREMENT_DELIVERABLE_ISSUE_TYPE_NAMES` の ID 版で、同じ filter から導出する。
 *
 * `issue_type_id` で絞る側（`issues_normalized_table` を直接引く BQ クエリ）が使う。
 * 現状の値は `REQUIREMENT_ASSIGN_CHILD_TYPE_IDS`（要件定義アサインで担当者を立てる子票）と
 * 一致するが、**軸が違う**ので別に持つ: あちらは「誰を立てるか」、こちらは「要件定義の成果物か」。
 * レビュー工程が requirementAssign ロールへ移るような運用変更があったとき、両者は分かれる。
 * @graph-connects none
 */
export const REQUIREMENT_DELIVERABLE_ISSUE_TYPE_IDS = REQUIREMENT_DEFINITION_SET_SPECS.filter((spec) => spec.issueTypeId !== DEV_TREE_ISSUE_TYPE_IDS.REQUIREMENT_REVIEW).map((spec) => spec.issueTypeId);
/**
 * 作成対象の子票定義を返す。軽タスクでは `lightTaskExcluded` の種別を外す。
 * @graph-connects none
 */
export function devChildSpecsFor(options) {
    return DEV_CHILD_SPECS.filter((spec) => !(options.lightTask && spec.lightTaskExcluded));
}
/**
 * 課題種別 ID から子票定義を引く。定義に無い種別（既存票だけが残る旧種別など）は undefined。
 *
 * 足りない子票を後から作る側が、件名ラベルと担当ロールを ID から引き直すのに使う。
 * @graph-connects none
 */
export function devChildSpecByIssueTypeId(issueTypeId) {
    return DEV_CHILD_SPECS.find((spec) => spec.issueTypeId === issueTypeId);
}
/**
 * 子票の件名（`【ラベル】案件名`）を組み立てる。
 * @graph-connects none
 */
export function devChildSummary(spec, projectName) {
    return `【${spec.summaryLabel}】${projectName}`;
}
/**
 * 作成時に担当者を解決すべきロールを返す。`requirementAssign` の種別は `undefined`
 * （= 担当者未設定で作る）。
 * @graph-connects none
 */
export function devChildCreationRole(spec) {
    return spec.assigneeRole === 'requirementAssign' ? undefined : spec.assigneeRole;
}
/**
 * ざっくり見積（最小 / 最大）から子票の予定pt を算出する。
 *
 * 予定pt = **ceil((最小 + 最大) / 2 × 比率)**。要件定義の成果物と総合テスト作成は案件規模に
 * 比例した固定割合で見積る運用のため、案件ごとに人が入れ直さず比率から導く。**切り上げ**なのは、
 * 比率が小さく（3〜4%）そのままでは 1pt 未満の端数になる案件が多く、0pt の票を作ると
 * 「見積が無い票」と区別できなくなるため。
 *
 * 比率を持たない種別・見積が片方でも欠けている場合は `null`（＝補完しない）。
 * @graph-connects none
 */
export function calcDevChildEstimatedHours(spec, roughEstimate) {
    const { min, max } = roughEstimate;
    if (spec.estimatePercent === undefined || min === null || max === null) {
        return null;
    }
    // (min + max) / 2 × percent / 100 を 1 回の割り算にまとめる（途中で小数を作らない）。
    return Math.ceil(((min + max) * spec.estimatePercent) / 200);
}
//# sourceMappingURL=dev-child-tickets.js.map